import { MESSAGE_VARIABLES, renderMessage } from '@crm/shared';
import { Button, Card, CardContent, Checkbox, Input, Label, Spinner, Textarea } from '@crm/ui';
import { Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Automation, AutomationInput } from '../api';

const EXEMPLO = Object.fromEntries(MESSAGE_VARIABLES.map((item) => [item.key, item.sample]));

interface AutomationCardProps {
  automation: Automation;
  readOnly: boolean;
  saving: boolean;
  onSave: (input: AutomationInput) => void;
  onTest: () => void;
  testing: boolean;
}

/**
 * Uma automacao. Desligada, o cartao e so o nome e o interruptor: nao adianta
 * mostrar campo de configuracao do que nao vai acontecer.
 */
export function AutomationCard({
  automation,
  readOnly,
  saving,
  onSave,
  onTest,
  testing,
}: AutomationCardProps) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [rascunho, setRascunho] = useState<AutomationInput>({});

  useEffect(() => {
    setRascunho({
      body: automation.body,
      couponId: automation.couponId,
      hoursBefore: automation.hoursBefore,
      sendWindowStart: automation.sendWindowStart,
      sendWindowEnd: automation.sendWindowEnd,
    });
  }, [automation]);

  function altera(valores: AutomationInput) {
    setRascunho((atual) => ({ ...atual, ...valores }));
  }

  function inserir(key: string) {
    const campo = bodyRef.current;
    const marca = `{{${key}}}`;
    const texto = rascunho.body ?? '';

    if (!campo) {
      altera({ body: `${texto}${marca}` });
      return;
    }

    const inicio = campo.selectionStart ?? texto.length;
    const fim = campo.selectionEnd ?? texto.length;

    altera({ body: `${texto.slice(0, inicio)}${marca}${texto.slice(fim)}` });
    requestAnimationFrame(() => {
      campo.focus();
      campo.setSelectionRange(inicio + marca.length, inicio + marca.length);
    });
  }

  const alterado = (
    ['body', 'couponId', 'hoursBefore', 'sendWindowStart', 'sendWindowEnd'] as const
  ).some((campo) => rascunho[campo] !== automation[campo]);

  return (
    <Card variant="flush">
      <CardContent className="space-y-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-medium">{automation.label}</h2>
            <p className="text-sm text-muted-foreground">{automation.hint}</p>
          </div>

          {/* O interruptor e a primeira coisa: e por ele que a maior parte das
              visitas a esta tela comeca e termina. */}
          <Checkbox
            className="shrink-0"
            label={automation.enabled ? 'Habilitada' : 'Desligada'}
            disabled={readOnly || saving}
            checked={automation.enabled}
            onChange={(input) => onSave({ enabled: input.target.checked })}
          />
        </div>

        {automation.enabled ? (
          <div className="space-y-4 border-t border-border pt-4">
            <div className="space-y-2">
              <Label htmlFor={`body-${automation.type}`}>Mensagem</Label>
              <Textarea
                id={`body-${automation.type}`}
                ref={bodyRef}
                rows={3}
                disabled={readOnly}
                value={rascunho.body ?? ''}
                onChange={(input) => altera({ body: input.target.value })}
              />

              <div className="flex flex-wrap gap-1.5 pt-1">
                {MESSAGE_VARIABLES.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    disabled={readOnly}
                    onClick={() => inserir(item.key)}
                    className="rounded-full border border-border-strong bg-card px-3 py-2 text-xs sm:px-2.5 sm:py-1 text-slate-600 transition-colors hover:bg-muted disabled:opacity-50 dark:text-slate-300"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <Label className="pt-1">Previa</Label>
              <p className="whitespace-pre-wrap rounded-card border border-border bg-muted/40 px-3 py-2 text-sm">
                {renderMessage(rascunho.body ?? '', EXEMPLO)}
              </p>
            </div>

            {automation.type === 'APPOINTMENT_REMINDER' ? (
              <div className="space-y-2">
                <Label htmlFor={`hours-${automation.type}`}>Quantas horas antes</Label>
                <Input
                  id={`hours-${automation.type}`}
                  type="number"
                  inputMode="numeric"
                  className="w-28"
                  disabled={readOnly}
                  value={rascunho.hoursBefore ?? ''}
                  onChange={(input) =>
                    altera({ hoursBefore: input.target.value ? Number(input.target.value) : null })
                  }
                />
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>Horario em que a mensagem pode sair</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  className="w-32"
                  aria-label="Inicio da janela"
                  disabled={readOnly}
                  value={rascunho.sendWindowStart ?? ''}
                  onChange={(input) => altera({ sendWindowStart: input.target.value || null })}
                />
                <span className="text-sm text-muted-foreground">as</span>
                <Input
                  type="time"
                  className="w-32"
                  aria-label="Fim da janela"
                  disabled={readOnly}
                  value={rascunho.sendWindowEnd ?? ''}
                  onChange={(input) => altera({ sendWindowEnd: input.target.value || null })}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="ghost" onClick={onTest} disabled={readOnly || testing}>
                {testing ? <Spinner /> : <Send className="h-4 w-4" />}
                Enviar teste para mim
              </Button>
              <Button onClick={() => onSave(rascunho)} disabled={readOnly || !alterado || saving}>
                {saving ? <Spinner /> : null}
                Salvar
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
